import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { marked } from "marked";
import Header from "./Header";
import Footer from "./Footer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

marked.setOptions({ breaks: true, gfm: true });

export default function BlogPost() {
    const { slug } = useParams();
    const [post, setPost] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    useEffect(() => {
        axios.get(`${API}/blog/${slug}`)
            .then((r) => {
                const data = r.data;
                setError(false);
                setPost(data);
                document.title = `${data.title} — FGM Apparel Blog`;
                const desc = document.querySelector('meta[name="description"]');
                desc?.setAttribute("content", data.excerpt);
                // Update OG tags so social-card crawlers that run JS (Twitter, FB)
                // see the right preview when /blog/:slug is shared directly.
                const ogImg = `https://fgmapparel.com/api/og/blog/${data.slug}.png`;
                const setMeta = (sel, val) => {
                    let el = document.querySelector(sel);
                    if (!el) {
                        el = document.createElement("meta");
                        const [, attr, attrVal] = sel.match(/\[(.+?)=["'](.+?)["']\]/) || [];
                        if (attr && attrVal) el.setAttribute(attr, attrVal);
                        document.head.appendChild(el);
                    }
                    el.setAttribute("content", val);
                };
                setMeta('meta[property="og:title"]', `${data.title} · FGM Apparel`);
                setMeta('meta[property="og:description"]', data.excerpt);
                setMeta('meta[property="og:image"]', ogImg);
                setMeta('meta[property="og:type"]', "article");
                setMeta('meta[property="og:url"]', `https://fgmapparel.com/blog/${data.slug}`);
                setMeta('meta[name="twitter:title"]', `${data.title} · FGM Apparel`);
                setMeta('meta[name="twitter:description"]', data.excerpt);
                setMeta('meta[name="twitter:image"]', ogImg);
                document.getElementById("fgm-article-jsonld")?.remove();
                const articleNode = {
                    "@context": "https://schema.org",
                    "@type": "BlogPosting",
                    "headline": data.title,
                    "description": data.excerpt,
                    "image": data.cover_image ? `https://fgmapparel.com${data.cover_image}` : "https://fgmapparel.com/brand/ambassador.jpeg",
                    "datePublished": data.published_at,
                    "dateModified": data.updated_at,
                    "author": { "@type": "Organization", "name": data.author || "FGM Editorial" },
                    "publisher": {
                        "@type": "Organization",
                        "name": "FGM Apparel",
                        "logo": { "@type": "ImageObject", "url": "https://fgmapparel.com/brand/White.png" },
                    },
                    "mainEntityOfPage": {
                        "@type": "WebPage",
                        "@id": `https://fgmapparel.com/blog/${data.slug}`,
                    },
                    "keywords": (data.tags || []).join(", "),
                };
                const el = document.createElement("script");
                el.id = "fgm-article-jsonld";
                el.type = "application/ld+json";
                el.text = JSON.stringify(articleNode);
                document.head.appendChild(el);
            })
            .catch(() => setError(true))
            .finally(() => setLoading(false));
        return () => document.getElementById("fgm-article-jsonld")?.remove();
    }, [slug]);

    const html = useMemo(() => (post ? marked.parse(post.body_md || "") : ""), [post]);

    return (
        <div className="App grain bg-fgm-dark min-h-screen" data-testid="blog-post-page">
            <Header />
            <main className="max-w-3xl mx-auto px-6 md:px-10 py-20">
                <Link
                    to="/blog"
                    data-testid="blog-back-link"
                    className="inline-flex items-center gap-2 text-zinc-400 hover:text-[#E63946] font-display tracking-[0.25em] uppercase text-xs mb-10 transition-colors"
                >
                    ← Back to Blog
                </Link>

                {loading && (
                    <div className="text-zinc-500 font-mono text-sm" data-testid="blog-post-loading">Loading…</div>
                )}

                {error && (
                    <div data-testid="blog-post-error">
                        <h1 className="font-display uppercase text-white text-3xl mb-4">Post not found</h1>
                        <p className="text-zinc-400">That piece may have been unpublished. <Link to="/blog" className="text-[#E63946] underline">See all posts →</Link></p>
                    </div>
                )}

                {post && (
                    <article data-testid={`blog-article-${post.slug}`}>
                        <div className="text-[10px] font-display tracking-[0.3em] uppercase text-zinc-500 mb-4">
                            {(post.tags || []).slice(0, 3).join(" · ") || "FGM Editorial"}
                        </div>
                        <h1 className="font-display uppercase text-white text-3xl sm:text-4xl md:text-5xl leading-[1.05] tracking-tight mb-4">
                            {post.title}
                        </h1>
                        <p className="text-zinc-400 text-base md:text-lg leading-relaxed mb-10">
                            {post.excerpt}
                        </p>
                        <div
                            className="fgm-blog-body text-zinc-200 leading-relaxed"
                            dangerouslySetInnerHTML={{ __html: html }}
                        />
                        <div className="mt-16 pt-10 border-t border-white/10 text-center">
                            <Link
                                to="/#shop"
                                data-testid="blog-cta-shop"
                                className="inline-block bg-[#E63946] text-white font-display tracking-[0.3em] uppercase text-xs px-8 py-4 hover:bg-white hover:text-black transition-colors"
                            >
                                Shop The Drop →
                            </Link>
                        </div>
                    </article>
                )}
            </main>
            <Footer />
        </div>
    );
}
